import { DocumentsDownloadType } from 'src/utils/constants/common';
import { generateGuid, getAxiosErrorMessage } from 'src/utils/CommonUtils';
import { WebsocketNotificationService } from '../ims-api/WebsocketNotification';
import { IMessage, UploadRequestStatuses } from 'src/shared/types/Document';
import { sha512 } from 'js-sha512';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  IAdditionalDocumentsRequest,
  ICasePassportRequest,
  IChecklistItemDocument,
  IChecklistItemGroupRequest,
  IChecklistItemLinkRequest,
  IDocument,
  IDocumentMetadata,
  IFileInfo,
  IImmigrationDocDraft,
  ISignedPutRequest,
  IUploadRequest,
} from 'src/shared/types/Documents';
import SparkMD5 from 'spark-md5';
import { BucketHttpClient, DocumentsAxiosInstance, ImsAxiosInstance } from '../axios/axios';

import { IPassportRequest, IPassportRequestForAllCasePassports } from 'src/shared/types/ImmigrationDocument';

export const createPassportForAllCasePassports = async (
  passportRequest: IPassportRequestForAllCasePassports
): Promise<any> => {
  const response = await DocumentsAxiosInstance.post(`/passport/allcasepassports`, passportRequest);
  return response;
};

export const createPassport = async (passportRequest: IPassportRequest): Promise<any> => {
  const response = await DocumentsAxiosInstance.post(`/passport`, passportRequest);
  return response;
};

export const deleteDocument = async (beneId: number, documentId: string, caseId?: number) => {
  return DocumentsAxiosInstance.delete(
    `documents/${beneId}/${documentId}?soft=true${caseId ? `&caseId=${caseId}` : ''}`
  );
};

const generateDeferredPromise = <T>() => {
  let resolve: (value: T | PromiseLike<T>) => void;
  let reject: (reason?: any) => void;

  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return {
    promise,
    resolve: resolve!,
    reject: reject!,
  };
};

const getDocumentStatus = async (id: string) => {
  const statusResponse = await DocumentsAxiosInstance.get('/status', { params: { id } });
  return statusResponse.data;
};

const waitTillCompletedOrFailed = async (requestId: string) => {
  const documentStatus = await getDocumentStatus(requestId);
  if (
    documentStatus.uploadRequestStatus === UploadRequestStatuses.Failed ||
    documentStatus.uploadRequestStatus === UploadRequestStatuses.Completed
  ) {
    return documentStatus;
  } else {
    return await waitTillCompletedOrFailed(requestId);
  }
};

export const getDocumentData = async (params: {
  downloadType: DocumentsDownloadType;
  caseId?: string;
  userId?: number;
  beneId?: number;
  documentKeys: string[];
  toastRef?: React.RefObject<IToastr | null>;
}) => {
  const toastRef = params.toastRef;
  try {
    const notificationId = generateGuid();
    const topic = `${params.userId}-document-download-${notificationId}`;
    const $subscription = generateDeferredPromise();
    await WebsocketNotificationService.WaitOnceAndThen(topic, $subscription);
    const { userId, toastRef: _, ...restParams } = params;
    const obj = { ...restParams, notificationId };
    const { data: downloadData } = await DocumentsAxiosInstance.post('/documents/files', obj);
    const documentStatus = await waitTillCompletedOrFailed(downloadData.id);

    if (documentStatus.uploadRequestStatus === UploadRequestStatuses.Failed) {
      throw new Error(`Download "${downloadData.id}" failed.`);
    } else if (documentStatus.uploadRequestStatus === UploadRequestStatuses.Queued) {
      await $subscription.promise;
    }

    try {
      const hash = sha512(`${downloadData.id}:${params.documentKeys[0]}`);
      const bucketParams = { hash: hash.toUpperCase(), hashToken: downloadData.hashToken, responseType: 'arraybuffer' };
      const presignedResponse = await BucketHttpClient.get(downloadData.url, bucketParams as AxiosRequestConfig);
      return {
        fileName: documentStatus.files[0].fileName,
        type: presignedResponse.headers['content-type'],
        arrayBuffer: [presignedResponse.data],
      };
    } catch (error) {
      const message = `"${downloadData.id}" failed.`;
      toastRef?.current?.clear();
      toastRef?.current?.show({ message, severity: 'error' });
      throw new Error(message);
    }
  } catch (error) {
    const errorMessage =
      error && typeof error === 'object' && 'response' in error
        ? getAxiosErrorMessage(error.response as AxiosResponse)
        : 'An unknown error occurred';
    toastRef?.current?.clear();
    toastRef?.current?.show({ message: errorMessage, severity: 'error' });
    throw error;
  }
};

export const unlinkDocumentsFromChecklistItem = async (
  links: { checklistItemId: number; documentId: number; beneId?: number }[]
): Promise<unknown> => {
  return await DocumentsAxiosInstance.post(`/casechecklist/unlinkdocuments`, links);
};

export const getAdditionalDocuments = async (caseId: number): Promise<IDocument[]> => {
  const data = await DocumentsAxiosInstance.get(`/casechecklist/additionaldocuments?caseId=${caseId}`);
  return data.data;
};

const readFileAsync = (file: File, start?: number, end?: number): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(<ArrayBuffer>reader.result);
    };

    reader.onerror = reject;

    if (start || end) {
      const blobSlice = File.prototype.slice || Blob.prototype.slice;
      reader.readAsArrayBuffer(blobSlice.call(file, start, end));
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
};

const hexToBase64 = (str: string): string => {
  let bString = '';
  for (var i = 0; i < str.length; i += 2) {
    bString += String.fromCharCode(parseInt(str.substr(i, 2), 16));
  }
  return btoa(bString);
};

const calculateMD5 = async (file: File): Promise<IFileInfo> => {
  let chunkSize = 2097152; // Read in chunks of 2MB
  let chunks = Math.ceil(file.size / chunkSize);

  let spark = new SparkMD5.ArrayBuffer();

  for (let currentChunk = 0; currentChunk < chunks; currentChunk++) {
    const start: number = currentChunk * chunkSize;
    const end: number = start + chunkSize >= file.size ? file.size : start + chunkSize;
    const md5Result: ArrayBuffer = await readFileAsync(file, start, end);
    spark.append(md5Result);
  }

  const hash: string = spark.end();
  const result: IFileInfo = { fileName: file.name, mD5: hexToBase64(hash) };
  return result;
};

const createImmigrationDocDraft = async (syncResults: IMessage[], beneId: number) => {
  const internalId = syncResults?.[0]?.content?.internalId;
  const docId = internalId ? parseInt(internalId, 10) : null;
  if (docId) {
    await DocumentsAxiosInstance.post('/immigrationdocdrafts', { docId, beneId, draftType: 'I-94' });
  }
};

export const getImmigrationDocDrafts = async (beneId: number): Promise<IImmigrationDocDraft[]> => {
  const response = await DocumentsAxiosInstance.get<IImmigrationDocDraft[]>('/immigrationdocdrafts', {
    params: { beneId },
  });
  return response.data;
};

interface UploadAndSyncOptions {
  files: File[];
  documentMetadata: IDocumentMetadata;
  userId: number;
  onPostUploadRequest?: (syncResults: IMessage[]) => Promise<void>;
}

const uploadAndSyncDocuments = async ({
  files,
  documentMetadata,
  userId,
  onPostUploadRequest,
}: UploadAndSyncOptions): Promise<{ syncResults: IMessage[]; uploadRequestResponse: ISignedPutRequest[] }> => {
  const fileInfos = await Promise.all(files.map(calculateMD5));
  const uploadRequest: IUploadRequest = { files: fileInfos, metadata: documentMetadata };
  const uploadRequestResponse = await DocumentsAxiosInstance.post<ISignedPutRequest[]>('/documents', uploadRequest);

  WebsocketNotificationService.initialize();
  const syncPromises: Promise<IMessage>[] = uploadRequestResponse.data.map((o) =>
    WebsocketNotificationService.listenTo(`${userId}-document-syncd-${o.id}`)
  );

  const amazonAxios = axios.create();
  await Promise.all(
    files.map(async (file, index) => {
      const uploadInfo = uploadRequestResponse.data[index];
      const fileInfo = fileInfos[index];
      const bucketUrl: string = uploadInfo.urls[0];
      const axiosConfig: AxiosRequestConfig = {
        headers: {
          'Content-Type': file.type,
          'Content-MD5': fileInfo.mD5,
          'x-amz-meta-document-security': uploadInfo.hashToken,
        },
      };
      return await amazonAxios.put(bucketUrl, file, axiosConfig);
    })
  );

  const syncResults = await Promise.all(syncPromises);

  if (onPostUploadRequest) {
    await onPostUploadRequest(syncResults);
  }

  return { syncResults, uploadRequestResponse: uploadRequestResponse.data };
};

export const uploadDocument = async (
  files: File[],
  documentMetadata: IDocumentMetadata,
  overrideDocType: number | undefined,
  docName: string,
  caseId: number,
  userId: number,
  checklistItemId?: number
): Promise<IDocument[]> => {
  const { syncResults } = await uploadAndSyncDocuments({ files, documentMetadata, userId });
  const newDocumentIds: number[] = syncResults.map((o) => parseInt(o.content?.internalId ?? '0'));

  if (checklistItemId) {
    let userTypes = ['BENEFICIARY', 'NETWORK_PARTNER_L1', 'NETWORK_PARTNER_L2', 'NETWORK_PARTNER_L3'];
    if (!documentMetadata.common.dependentId) {
      userTypes = [...userTypes, 'HR_CONTACT', 'HR_LEAD'];
    }
    const linkDocumentsRequest = newDocumentIds.map<IChecklistItemLinkRequest>((documentId) => ({
      checklistItemId,
      documentId,
      docTypeId: overrideDocType,
      beneId: documentMetadata.common.beneId ?? 0,
      caseId,
      docName,
      userTypes,
    }));
    const linkDocumentToChecklistResponse = await DocumentsAxiosInstance.post<IChecklistItemDocument[]>(
      '/casechecklist/documents',
      linkDocumentsRequest
    );
    return linkDocumentToChecklistResponse.data.map<IDocument>((o) => ({
      id: (o.document?.docId ?? 0).toString(),
      documentApiKey: o.document?.documentApiKey ?? '',
      documentName: o.document?.docName ?? '',
      documentFiles: o.document?.documentFiles ?? [],
    }));
  }

  return syncResults.map<IDocument>((o) => ({
    id: o.content?.internalId ?? '',
    documentApiKey: o.content?.id ?? '',
    documentName: o.content?.name ?? '',
    documentFiles: [{ fileName: o.content?.name ?? '' }],
  }));
};

export const uploadI94Document = async (
  files: File[],
  documentMetadata: IDocumentMetadata,
  userId: number,
  beneId: number
): Promise<IDocument[]> => {
  const { syncResults } = await uploadAndSyncDocuments({
    files,
    documentMetadata,
    userId,
    onPostUploadRequest: (results) => createImmigrationDocDraft(results, beneId),
  });

  return syncResults.map<IDocument>((o) => ({
    id: o.content?.internalId ?? '',
    documentApiKey: o.content?.id ?? '',
    documentName: o.content?.name ?? '',
    documentFiles: [{ fileName: o.content?.name ?? '' }],
  }));
};

export const linkDocumentToChecklistItemGroup = async (
  checklistItemGroupId: number,
  documentId: number
): Promise<IChecklistItemDocument> => {
  const response = await DocumentsAxiosInstance.post<IChecklistItemDocument>(
    `/casechecklistitemgroup/link?checklistItemGroupId=${checklistItemGroupId}&documentId=${documentId}`
  );
  return response.data;
};

export const removePassportDocuments = async (links: {
  caseId: number;
  checklistItemGroupId?: number;
  checklistItemId?: number;
  documentId?: number;
}): Promise<any> => {
  return await DocumentsAxiosInstance.post(`/casechecklist/removedocuments`, links);
};

export const linkDocumentsToChecklistItem = async (links: IChecklistItemLinkRequest[]): Promise<void> => {
  await DocumentsAxiosInstance.post<void>(`/casechecklist/documents`, links);
  return;
};

export const addAdditionalDocuments = async (links: IAdditionalDocumentsRequest[]): Promise<unknown> => {
  return await DocumentsAxiosInstance.post(`/casechecklist/addadditionaldocuments`, links);
};

export const updatePassportChecklistGroup = async (request: IChecklistItemGroupRequest): Promise<void> => {
  await DocumentsAxiosInstance.post<void>(`/passport/checklistgroup`, request);
  return;
};

export const updatePassportForCase = async (request: ICasePassportRequest): Promise<void> => {
  await DocumentsAxiosInstance.post<void>(`/passport/case`, request);
  return;
};
